<template>
  <div class="run-selector d-flex align-center flex-wrap">
    <v-menu offset-y :close-on-content-click="true" max-height="360" eager>
      <template #activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          small
          outlined
          class="run-selector__activator text-none"
          :loading="loading"
          :disabled="busy"
          v-on="on"
        >
          <v-icon small left>mdi-history</v-icon>
          <span class="run-selector__current">{{ currentLabel }}</span>
          <v-icon small right>mdi-menu-down</v-icon>
        </v-btn>
      </template>
      <v-list dense class="run-selector__list">
        <v-list-item
          class="run-selector__item run-selector__item--active-pointer"
          :input-value="currentRunId == null"
          @click="select(null)"
        >
          <v-list-item-icon class="mr-2">
            <v-icon small>mdi-star-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Active run</v-list-item-title>
            <v-list-item-subtitle>Whatever the pointer of this video selects</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider v-if="items.length" />
        <v-list-item
          v-for="item in items"
          :key="item.id"
          class="run-selector__item"
          :class="{ 'run-selector__item--current': item.id === currentRunId }"
          :input-value="item.id === currentRunId"
          @click="select(item.id)"
        >
          <v-list-item-content>
            <v-list-item-title class="run-selector__item-label">{{ item.label }}</v-list-item-title>
            <v-list-item-subtitle v-if="item.note" class="run-selector__item-note">
              {{ item.note }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action class="run-selector__item-status my-0">
            <div class="d-flex align-center">
              <v-icon v-if="item.isActive" x-small color="success" class="mr-1" title="Active">
                mdi-star
              </v-icon>
              <v-icon v-if="item.isPinned" x-small color="info" class="mr-1" title="Pinned">
                mdi-pin
              </v-icon>
              <RunStatusChip :status="item.status" x-small :show-icon="false" />
            </div>
          </v-list-item-action>
        </v-list-item>
        <v-list-item v-if="!items.length && !loading" disabled>
          <v-list-item-content>
            <v-list-item-subtitle>No runs recorded for this video yet</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>

    <template v-if="!readOnly && hasLedgerRun">
      <v-btn
        v-if="canPromote"
        small
        text
        color="primary"
        class="run-selector__action run-selector__action--promote ml-1 text-none"
        :disabled="busy"
        title="Make this run the active one for this video"
        @click="$emit('promote', currentRun)"
      >
        <v-icon small left>mdi-star-outline</v-icon>
        Promote
      </v-btn>
      <v-btn
        v-if="canPin"
        small
        text
        color="primary"
        class="run-selector__action run-selector__action--pin ml-1 text-none"
        :disabled="busy"
        title="Pin this run: new runs will not replace it"
        @click="$emit('pin', currentRun)"
      >
        <v-icon small left>mdi-pin-outline</v-icon>
        Pin
      </v-btn>
      <v-btn
        v-if="canReplay"
        small
        text
        color="primary"
        class="run-selector__action run-selector__action--replay ml-1 text-none"
        :disabled="busy"
        title="Run again with this run's configuration and engines"
        @click="$emit('replay', currentRun)"
      >
        <v-icon small left>mdi-replay</v-icon>
        Replay
      </v-btn>
      <v-btn
        v-if="canUnpin"
        small
        text
        color="primary"
        class="run-selector__action run-selector__action--unpin ml-1 text-none"
        :disabled="busy"
        title="Unpin: later runs may become active again"
        @click="$emit('unpin', currentRun)"
      >
        <v-icon small left>mdi-pin-off-outline</v-icon>
        Unpin
      </v-btn>
    </template>
  </div>
</template>

<script>
import { isTerminalRunStatus, normalizeRunStatus } from '@/store/modules/analysisQueue';
import { isLegacyRun, runIdOf, runLabel, sortRunsForSelector } from '@/utils/runLabels';
import RunStatusChip from './RunStatusChip.vue';

/**
 * Dropdown of the runs of one video ('Run #n · batch · config vN · 2 h ago', SUCCEEDED first)
 * plus the Promote / Pin / Unpin actions for the run being viewed.
 *
 * Emits:
 *  - select(runId|null): the user picked a run (null = follow the active pointer)
 *  - promote(run) / pin(run) / unpin(run): control actions on the current run
 */
export default {
  name: 'RunSelector',

  components: { RunStatusChip },

  props: {
    /** Ledger run documents for the source (GET /analysis-runs?source_type&source_id). */
    runs: {
      type: Array,
      default: () => [],
    },
    /** Run block of the analysis being viewed (metadata.run); null for legacy results. */
    currentRun: {
      type: Object,
      default: null,
    },
    /** Run id from the route (`?run=`); null when following the active pointer. */
    currentRunId: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /** A control action is in flight. */
    busy: {
      type: Boolean,
      default: false,
    },
    /** Hide Promote / Pin / Unpin (shared, read-only views). */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /** Reference instant for relative times (tests). */
    now: {
      type: Number,
      default: null,
    },
  },

  computed: {
    referenceNow() {
      return this.now ?? Date.now();
    },
    activeRunId() {
      return this.currentRun?.is_active ? runIdOf(this.currentRun) : null;
    },
    pinnedRunId() {
      return this.currentRun?.is_pinned ? runIdOf(this.currentRun) : null;
    },
    items() {
      return sortRunsForSelector(this.runs).map((run) => {
        const id = runIdOf(run);
        return {
          id,
          run,
          label: runLabel(run, { now: this.referenceNow }),
          note: run.note || null,
          status: run.status,
          isActive: id != null && id === this.activeRunId,
          isPinned: id != null && id === this.pinnedRunId,
        };
      });
    },
    hasLedgerRun() {
      return !isLegacyRun(this.currentRun);
    },
    currentLabel() {
      if (this.currentRun) {
        const label = runLabel(this.currentRun, { now: this.referenceNow });
        return this.currentRunId == null && this.currentRun.is_active
          ? `${label} (active)`
          : label;
      }
      return this.currentRunId == null ? 'Active run' : `Run ${this.currentRunId.slice(0, 8)}`;
    },
    currentSucceeded() {
      return normalizeRunStatus(this.currentRun?.status) === 'SUCCEEDED';
    },
    canPromote() {
      return this.currentSucceeded && !this.currentRun.is_active;
    },
    canPin() {
      return this.currentSucceeded && !this.currentRun.is_pinned;
    },
    canUnpin() {
      return !!this.currentRun?.is_pinned;
    },
    /** Replay needs a finished run with the stored job payload (holding pen, M3 onwards). */
    canReplay() {
      return isTerminalRunStatus(this.currentRun?.status);
    },
  },

  methods: {
    select(runId) {
      if ((runId ?? null) === (this.currentRunId ?? null)) return;
      this.$emit('select', runId);
    },
  },
};
</script>

<style scoped>
.run-selector__current {
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.run-selector__item--current {
  background: rgba(25, 118, 210, 0.06);
}
</style>
